import { isResourceOfType, OpalApiResponseDocument } from '@opallabs/api/v3'
import type {
  ApprovalResponse,
  Assignment,
  StageTypes,
  TaskResponse,
  Workflow,
  WorkflowStatus,
} from '../../../types'
import {
  buildAssignments,
  buildResponses,
  buildStatus,
  buildWorkflow,
  WorkflowResource,
} from '../selectors'

type WorkflowsDocument = OpalApiResponseDocument<'ReadWorkflowsV3', 200>

type Progress = WorkflowsDocument['data'][number]['attributes']['progress']

// The shared selectors read v2's `status`; v3 has no such field, so derive it
// from `progress`. A workflow that has not started is `inactive`, as it is for
// `buildStatus`: it waits at its start gate.
const statusByProgress: Record<
  Progress,
  WorkflowResource['attributes']['status']
> = {
  not_started: 'inactive',
  tasks_pending: 'active',
  approval_pending: 'active',
  declined: 'active',
  paused: 'paused',
  tasks_complete: 'complete',
  approved: 'complete',
}

const asWorkflowResource = (
  workflow: WorkflowsDocument['data'][number] | undefined
): WorkflowResource | undefined =>
  workflow && {
    ...workflow,
    attributes: {
      ...workflow.attributes,
      status: statusByProgress[workflow.attributes.progress],
    },
  }

export const selectWorkflow = (document: WorkflowsDocument): Workflow | null =>
  buildWorkflow({
    workflow: asWorkflowResource(document.data[0]),
    // No `deleted_at` filter here: v3 does not expose one.
    stages: (document.included ?? []).filter(
      isResourceOfType('workflow_stage', 'V3')
    ),
  })

export const selectStatus = (
  document: WorkflowsDocument
): WorkflowStatus | null => {
  const included = document.included ?? []

  return buildStatus({
    workflow: asWorkflowResource(document.data[0]),
    assignments: included.filter(isResourceOfType('workflow_assignment', 'V3')),
    responses: included.filter(isResourceOfType('workflow_response', 'V3')),
  })
}

export const selectAssignments = (
  document: OpalApiResponseDocument<'ReadWorkflowStageV3', 200>
): Array<Assignment> =>
  buildAssignments(
    (document.included ?? []).filter(
      isResourceOfType('workflow_assignment', 'V3')
    )
  )

export const selectResponses = (
  document: OpalApiResponseDocument<'ReadWorkflowAssignmentV3', 200>,
  stageType: StageTypes
): Array<TaskResponse | ApprovalResponse> =>
  buildResponses(
    (document.included ?? []).filter(
      isResourceOfType('workflow_response', 'V3')
    ),
    stageType
  )
